/**
 * Calculated / rule-based fields — shared frontend helpers (TRUS-383 Phase 2).
 *
 * The backend derives these values on read, so most surfaces only need to know
 * "is this field calculated?" to render it read-only. Two cases still need the
 * calculation client-side:
 *
 *  1. **Live editing.** While an admin is typing a date of birth, the age has to
 *     update before anything is saved. Waiting for a round trip would show a
 *     stale (or empty) age next to the value that produced it.
 *  2. **A field the org hasn't configured yet.** Phase 1 shipped Age as a name
 *     heuristic (`isAgeField`); until an org declares the rule, that heuristic
 *     is still the only thing making Age read-only. It stays as a fallback so
 *     nothing regresses — but a configured rule always wins.
 *
 * The evaluation here mirrors `app/services/computed_fields.py` exactly,
 * including the two rules that stop a derived field from showing a wrong-looking
 * number: a future date and an implausible one (production holds
 * `dob = 0001-01-01`, which naive arithmetic renders as "2025") both yield
 * nothing rather than a value.
 */

import type { ComputedFormula } from '@/services/api/members';

/** The shape every caller has in common: a field definition with an optional rule. */
export interface DerivableField {
  name?: string;
  friendly_name?: string;
  type?: string;
  computed_formula?: ComputedFormula | null;
}

/** Matches `_MAX_PLAUSIBLE_YEARS` in app/services/computed_fields.py. */
const MAX_PLAUSIBLE_YEARS = 150;

export const CALC_TYPE_LABELS: Record<string, string> = {
  date_diff: 'Date difference (e.g. Age from Date of Birth)',
  sum: 'Sum',
  list_collect: 'Collect into a list',
};

export const ACROSS_LABELS: Record<string, string> = {
  household_members: "The household's members",
  household_family_members: "Those members' family members",
  household_people: 'Everyone in the household (members + family members)',
};

export const UNIT_LABELS: Record<string, string> = {
  years: 'Years (age)',
  months: 'Months',
  days: 'Days',
};

/** True when a rule makes this field calculated — i.e. read-only to the user. */
export function isDerived(field: DerivableField | null | undefined): boolean {
  return !!field?.computed_formula?.fn;
}

export function isCrossRecord(rule: ComputedFormula | null | undefined): boolean {
  return rule?.scope === 'cross_record';
}

/** The field names a same-record rule reads, so a UI can watch them. */
export function ruleSourceNames(rule: ComputedFormula | null | undefined): string[] {
  if (!rule) return [];
  if (isCrossRecord(rule)) return rule.source != null ? [String(rule.source)] : [];
  return (rule.inputs ?? []).map((i) => String(i));
}

/** Parse the date formats these free-text columns actually hold. Mirrors to_date(). */
export function parseDateValue(value: unknown): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    // Reject a rolled-over date (e.g. 2026-13-45) rather than accepting the roll.
    if (d.getFullYear() !== Number(m[1]) || d.getMonth() !== Number(m[2]) - 1) return null;
    return d;
  }
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (m) {
    const d = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
    if (d.getMonth() !== Number(m[1]) - 1) return null;
    return d;
  }
  return null;
}

/**
 * Whole units elapsed from `value` to `today`, or null when not derivable.
 * Mirrors `date_diff()`: blank/unparseable, future, and implausibly old all
 * yield null so the field renders empty instead of showing a bogus number.
 */
export function dateDiff(value: unknown, unit: string = 'years', today: Date = new Date()): number | null {
  const d = parseDateValue(value);
  if (!d) return null;
  const ref = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (d > ref) return null;
  let months = (ref.getFullYear() - d.getFullYear()) * 12 + (ref.getMonth() - d.getMonth());
  if (ref.getDate() < d.getDate()) months -= 1;
  const years = Math.max(Math.floor(months / 12), 0);
  if (years > MAX_PLAUSIBLE_YEARS) return null;
  if (unit === 'days') return Math.floor((ref.getTime() - d.getTime()) / 86_400_000);
  if (unit === 'months') return Math.max(months, 0);
  return years;
}

function toNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(String(value).replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

/**
 * Evaluate a SAME-RECORD rule against one record's values. Returns null when the
 * value isn't derivable, so the caller renders an empty state rather than 0.
 *
 * Cross-record rules are deliberately not evaluated here: they need the
 * household's other people, which the client doesn't have. Those values arrive
 * from the backend already computed.
 */
export function evaluateSameRecord(
  rule: ComputedFormula | null | undefined,
  values: Record<string, unknown>,
  today: Date = new Date(),
): number | string | unknown[] | null {
  if (!rule?.fn || isCrossRecord(rule)) return null;
  const inputs = (rule.inputs ?? []).map((i) => String(i));
  const read = (key: string) => values[key] ?? values[String(key)];

  switch (rule.fn) {
    case 'date_diff':
      return inputs.length ? dateDiff(read(inputs[0]), rule.unit ?? 'years', today) : null;
    case 'list_collect': {
      const out = inputs.map(read).filter((v) => v !== null && v !== undefined && v !== '');
      return out.length ? out : null;
    }
    case 'sum': {
      const nums = inputs.map((k) => toNumber(read(k))).filter((n): n is number => n !== null);
      return nums.length ? nums.reduce((a, b) => a + b, 0) : null;
    }
    default:
      // Any other op (legacy Forms fns, or a rule owned by another subsystem)
      // is left to the backend — the read already carries its value.
      return null;
  }
}

/** Display form of a derived value: a list joins, nothing renders as an em dash. */
export function formatDerived(value: unknown): string {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  return String(value);
}

/** One-line human description of a rule, for the read-only hint under a field. */
export function describeRule(rule: ComputedFormula | null | undefined): string {
  if (!rule?.fn) return '';
  const sources = ruleSourceNames(rule).join(', ') || 'a source field';
  if (rule.fn === 'date_diff') {
    const unit = (rule.unit ?? 'years') as string;
    return `Calculated: ${unit} since ${sources}`;
  }
  const across = isCrossRecord(rule) ? ACROSS_LABELS[String(rule.across ?? 'household_people')] : 'this record';
  if (rule.fn === 'sum') return `Calculated: total of ${sources} across ${across}`;
  if (rule.fn === 'list_collect') return `Calculated: ${sources} collected from ${across}`;
  return 'Calculated automatically';
}

// ---------------------------------------------------------------------------
// Group-child derivation (TRUS-226 heuristic + TRUS-392 rules)
//
// These are pure predicates over a field definition, so they live here rather
// than in the renderer: importing the renderer pulls in the API layer, which
// touches localStorage at module scope and can't be loaded in a unit test.
// RepeatableGroupRenderer re-exports them for existing callers.
// ---------------------------------------------------------------------------

export function fieldLabel(c: DerivableField): string {
  return `${c.name ?? ''} ${c.friendly_name ?? ''}`;
}

export function isDobField(c: DerivableField): boolean {
  if (c.type !== 'date' && c.type !== 'fixed_date') return false;
  return /date.?of.?birth|\bdob\b|birth.?date/i.test(fieldLabel(c));
}

export function isAgeField(c: DerivableField): boolean {
  if (c.type !== 'int') return false;
  return /\bage\b/i.test(fieldLabel(c));
}

/**
 * Resolve the DOB source field name for a group (legacy TRUS-226 heuristic).
 * Only pairs when the group has exactly one DOB field and at least one age
 * field, so ambiguous multi-DOB groups fall through to editable rendering.
 */
export function resolveDobSourceName(children: DerivableField[]): string | null {
  const dobs = children.filter(isDobField);
  const ages = children.filter(isAgeField);
  if (dobs.length === 1 && ages.length >= 1) return dobs[0].name ?? null;
  return null;
}

export interface ChildDerivation {
  rule: ComputedFormula;
  /** 'rule' = the admin configured it; 'heuristic' = inferred from field names. */
  source: 'rule' | 'heuristic';
}

/**
 * How one group child gets its value: from a configured rule, from the legacy
 * DOB/age heuristic, or from the user (null).
 *
 * A CONFIGURED rule always wins — it says what the admin actually declared,
 * for any calculation and any field name. The heuristic remains only so orgs
 * that haven't declared a rule yet keep the behaviour they have today.
 */
export function resolveChildDerivation(
  child: DerivableField,
  heuristicDobName: string | null,
): ChildDerivation | null {
  const configured = child.computed_formula;
  if (configured?.fn) return { rule: configured, source: 'rule' };
  if (heuristicDobName && isAgeField(child)) {
    return {
      rule: { fn: 'date_diff', scope: 'same_record', inputs: [heuristicDobName], unit: 'years' },
      source: 'heuristic',
    };
  }
  return null;
}
